import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth';
import SocialLogin from '../components/SocialLogin';

const DeviceLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userCode, setUserCode] = useState(searchParams.get('code') || '');
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleApprove = useCallback(async () => {
    const CODE_PATTERN = /^[A-Z]{4}-\d{4}$/;
    if (!userCode || !CODE_PATTERN.test(userCode.toUpperCase())) {
      setError('Please enter a valid code (ABCD-1234 format)');
      return;
    }

    setIsApproving(true);
    setError('');

    try {
      await authApi.deviceApprove({ user_code: userCode.toUpperCase() });
      setSuccess(true);
      setTimeout(() => {
        navigate('/device-authorized');
      }, 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to authorize device';
      setError(errorMessage);
    } finally {
      setIsApproving(false);
    }
  }, [userCode, navigate]);

  // Auto-approve if logged in and code in URL
  useEffect(() => {
    if (user && userCode && !success && !error) {
      handleApprove();
    }
  }, [user, userCode, success, error, handleApprove]);

  const handleDeny = async () => {
    setIsApproving(true);
    setError('');

    try {
      await authApi.deviceDeny({ user_code: userCode.toUpperCase() });
      navigate('/');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to deny device';
      setError(errorMessage);
    } finally {
      setIsApproving(false);
    }
  };

  // If not logged in, show login page with OAuth
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Device Login</h1>
            <p className="text-gray-600 mt-2">
              Sign in to authorize your device
            </p>
            {userCode && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Code from your device:</p>
                <p className="text-xl font-mono font-bold text-blue-600 tracking-wider mt-1">
                  {userCode}
                </p>
              </div>
            )}
          </div>

          <SocialLogin />

          <div className="mt-6 text-center text-sm text-gray-500">
            After signing in, you'll approve the device automatically.
          </div>
        </div>
      </div>
    );
  }

  // User is logged in - show approval interface
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {success ? (
          // Success state
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full mx-auto flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Device Authorized!</h1>
            <p className="text-gray-600">
              Your device is now connected. Redirecting...
            </p>
          </div>
        ) : (
          // Approval form
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Authorize Device</h1>
              <p className="text-gray-600 mt-2">
                {userCode ? 'Confirm the code matches your device' : 'Enter the code shown on your device'}
              </p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                placeholder="ABC-1234"
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-wider border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none uppercase"
                maxLength={8}
                disabled={isApproving}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleDeny}
                disabled={isApproving || userCode.length < 7}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition"
              >
                {isApproving ? 'Processing...' : 'Deny'}
              </button>
              <button
                onClick={handleApprove}
                disabled={isApproving || userCode.length < 7}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
              >
                {isApproving ? 'Authorizing...' : 'Authorize Device'}
              </button>
            </div>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Logged in as <span className="font-medium">{user.email}</span></p>
              <button
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:underline mt-2"
              >
                Use a different account
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceLogin;
